import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon, EnterIcon } from "@radix-ui/react-icons";
import { Button, Card, Flex, Spinner } from "@radix-ui/themes";
import { useForm } from "react-hook-form";

import { isBoolean, isEmpty } from "lodash";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { ExhibitorType } from "../../../types/ScientificModule";
import { routes } from "../../../routes";
import TextFieldComponent from "../../../components/inputs/Textfield/TextfieldComponent";
import { createExhibitor } from "../../../services/scientific/exhibitor";
import { createUser } from "../../../services/user/createUser";
import { key_userBi, key_userNuit } from "../../../utils/constants";
import { getUserByBI, getUserByNuit } from "../../../services/user/getUsers";
import { useCallback } from "react";
import { debounce } from "lodash";
import DateFieldComponent from "../../../components/inputs/Datefield/DatefieldComponent";
import RadioGroupComponent from "../../../components/inputs/RadioGroup/RadioGroupComponent";
import { CreateExhibitorFormValidation } from "./CreateExhibitorFormValidation";
import TextAreaFieldComponent from "../../../components/inputs/TextAreafield/TextAreafieldComponent";
import SelectSearchableComponent from "../../../components/inputs/Select/SelectSearchableComponent";
import { Countries, Country } from "../../../utils/codes";

export const CreateExhibitor = () => {
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: createExhibitor,
    mutationKey: "createExhibitor",
  });
  const mutationCreateUser = useMutation({
    mutationFn: createUser,
    mutationKey: "createUser",
  });
  const defaultValues: ExhibitorType = {
    id: "",
    idUser: "",
    idStudent: "",
    address: "",
    addressCoords: "",
    country: "",
    status: true,
    user: {
      id: undefined,
      name: "",
      userName: "",
      email: "",
      bi: "",
      nuit: "",
      phoneNumber: "",
      dateOfBirth: "",
      gender: "M",
      role: undefined,
      icon: undefined,
      lockoutEnabled: undefined,
      password: undefined,
      roles: undefined,
      claims: undefined,
    },
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
    watch,
    reset,
    resetField,
  } = useForm<ExhibitorType>({
    resolver: yupResolver(CreateExhibitorFormValidation),
    defaultValues,
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUseNativeValidation: false,
  });
  const watchIsForeign = watch("user.isForeign");
  const handleOnChangeIsForeign = (e: any) => {
    const isIsForeign = stringToBoolean(getValues("user.isForeign"));
    if (isIsForeign) {
      resetField("user.nuit");
      setValue("user.countryDescription", "");
    }
    if (!isIsForeign) {
      setValue("user.countryDescription", "MOZ");
    }
  };
  const stringToBoolean = (str?: any): boolean => {
    if (!str) return false;
    try {
      if (isBoolean(str)) {
        return str;
      } else {
        return str.toLowerCase() === "true";
      }
    } catch (ex) {
      return false;
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (!data.idUser || !data.user?.id) {
        const resultCreateUser = await mutationCreateUser.mutateAsync(
          data.user!
        );
        if (!resultCreateUser) return;
        const { id: idUser } = resultCreateUser as any;
        data.idUser = idUser;
        data.fullname = data.user.name;
      }
      const result = await mutation.mutateAsync({
        ...data,
        fullname: data.user.name,
      });
      if (result) {
        toast.success("Registado!");
        handleBack();
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });

  const { isLoading: loadingUserByBi, refetch: fetchUserByBi } = useQuery(
    [key_userBi],
    async () => {
      try {
        const userData = await getUserByBI(getValues("searchBi")!);
        if (!userData) return;
        reset({
          searchBi: getValues("searchBi")!,
          idUser: userData.id,
          fullname: userData.name,
          status: true,
          user: userData,
        });
        return userData;
      } catch (error) {}
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
  const { isLoading: loadingUserByNuit, refetch: fetchUserByNuit } = useQuery(
    [key_userNuit],
    async () => {
      try {
        const userData = await getUserByNuit(getValues("searchNuit")!);
        if (!userData) return;
        reset({
          searchBi: getValues("searchBi")!,
          idUser: userData.id,
          fullname: userData.name,
          status: true,
          user: userData,
        });
        return userData;
      } catch (error) {}
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const handleBIChange = useCallback(
    debounce((value: string) => {
      const trimmedValue = value.trim();
      setValue("searchBi", trimmedValue);
      if (trimmedValue.length >= 10) {
        fetchUserByBi();
      } else {
      }
    }, 300),
    [setValue, fetchUserByBi]
  );
  const handleNUITChange = useCallback(
    debounce((value: string) => {
      const trimmedValue = value.trim();
      setValue("searchNuit", trimmedValue);
      if (trimmedValue.length >= 8) {
        fetchUserByNuit();
      } else {
      }
    }, 300),
    [setValue, fetchUserByNuit]
  );
  const handleFullnameChange = (e: any) => {
    const fullname = e.target.value;
    setValue("user.name", fullname);
    setValue("fullname", fullname);
  };

  const handleEmailChange = (e: any) => {
    const fullname = e.target.value;
    setValue("user.email", fullname);
    setValue("user.userName", fullname);
  };

  const handleBack = () => {
    navigate(routes.exhibitors);
  };

  return (
    <Card size="3">
      <Flex direction="row" gap="6">
        <Flex direction="column" gap="5" style={{ flex: 1 }}>
          <form onSubmit={onSubmit} noValidate>
            <Flex direction="column" gap="4">
              <Flex gap="2">
                <TextFieldComponent
                  style={{ width: "100%" }}
                  name="searchBi"
                  label="Procurar por BI"
                  control={control}
                  errors={errors}
                  fieldProps={{
                    placeholder: "Procurar por BI",
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      handleBIChange(e.target.value),
                    onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData("text");
                      handleBIChange(pastedText);
                    },
                  }}
                />
                <TextFieldComponent
                  style={{ width: "100%" }}
                  name="searchNuit"
                  label="Procurar por NUIT"
                  control={control}
                  errors={errors}
                  fieldProps={{
                    placeholder: "Procurar por NUIT",
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      handleNUITChange(e.target.value),
                    onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData("text");
                      handleNUITChange(pastedText);
                    },
                  }}
                />
              </Flex>
              <Flex gap="8">
                <TextFieldComponent
                  name="user.name"
                  composedName={{ parent: "user", prop: "name" }}
                  label="Nome"
                  control={control}
                  errors={errors}
                  fieldProps={{
                    placeholder: "Nome completo",
                    onChange: handleFullnameChange,
                    type: "text",
                  }}
                  style={{ width: "100%" }}
                />
                <TextFieldComponent
                  name="user.userName"
                  composedName={{ parent: "user", prop: "userName" }}
                  label="Utilizador"
                  control={control}
                  errors={errors}
                  fieldProps={{
                    placeholder: "Utilizador",
                    disabled: "disabled",
                  }}
                  style={{ width: "100%" }}
                />
                <TextFieldComponent
                  name="user.email"
                  composedName={{ parent: "user", prop: "email" }}
                  label="Email"
                  control={control}
                  errors={errors}
                  fieldProps={{
                    placeholder: "seu@email.com",
                    onChange: handleEmailChange,
                    type: "email",
                  }}
                  style={{ width: "100%" }}
                />
              </Flex>
              <Flex gap="8">
                <TextFieldComponent
                  name="user.bi"
                  composedName={{ parent: "user", prop: "bi" }}
                  label="BI"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "BI" }}
                  style={{ width: "100%" }}
                />
                <TextFieldComponent
                  name="user.nuit"
                  composedName={{ parent: "user", prop: "nuit" }}
                  label="NUIT"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "Seu NUIT" }}
                  style={{ width: "100%" }}
                />
                <TextFieldComponent
                  name="user.phoneNumber"
                  composedName={{ parent: "user", prop: "phoneNumber" }}
                  label="Telefone"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "123456789" }}
                  style={{ width: "100%" }}
                />
              </Flex>
              <Flex gap="8">
                <DateFieldComponent
                  name="user.dateOfBirth"
                  composedName={{ parent: "user", prop: "dateOfBirth" }}
                  label="Data de Nascimento"
                  control={control}
                  errors={errors}
                  style={{ width: "32%" }}
                />
                <RadioGroupComponent
                  name="user.gender"
                  composedName={{ parent: "user", prop: "gender" }}
                  label="Gênero"
                  control={control}
                  errors={errors}
                  style={{ width: "68%" }}
                  options={[
                    { value: "M", label: "Masculino" },
                    { value: "F", label: "Feminino" },
                  ]}
                  fieldProps={{
                    style: {
                      display: "flex",
                      flexDirection: "row",
                      gap: "16px",
                    },
                  }}
                />
              </Flex>
              <Flex gap="6">
                <RadioGroupComponent
                  name="user.isForeign"
                  label="Estrangeiro"
                  control={control}
                  errors={errors}
                  style={{ width: "50%" }}
                  options={[
                    { value: "true", label: "Sim" },
                    { value: "false", label: "Não" },
                  ]}
                  fieldProps={{
                    style: {
                      display: "flex",
                      flexDirection: "row",
                      gap: "16px",
                    },
                    onChange: handleOnChangeIsForeign,
                  }}
                />
                <SelectSearchableComponent
                  name="user.countryDescription"
                  label="País"
                  control={control}
                  errors={errors}
                  options={Countries?.map((country: Country) => ({
                    value: country.code,
                    label: country.label ?? "",
                  }))}
                  style={{ width: "100%" }}
                  fieldProps={{ disabled: !stringToBoolean(watchIsForeign) }}
                />
              </Flex>
              <Flex gap="8">
                <TextAreaFieldComponent
                  name="address"
                  label="Endereço"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "Endereço" }}
                  style={{ width: "100%" }}
                />
              </Flex>
              <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
                <Button
                  color="red"
                  onClick={(e) => {
                    e.preventDefault();
                    handleBack();
                  }}
                >
                  Sair
                </Button>
                <Button
                  type="submit"
                  variant="surface"
                  disabled={mutation.isLoading}
                >
                  {mutation.isLoading ? <Spinner loading /> : "Registar"}
                </Button>
              </Flex>
            </Flex>
          </form>
        </Flex>
      </Flex>
    </Card>
  );
};
